import { useSubmitCreateTransactionDetailOption } from '@/features/transaction_items/Detail/hooks/useSubmitCreateTransactionDetailOption';
import { useSubmitUpdateTransactionDetailOption } from '@/features/transaction_items/Detail/hooks/useSubmitUpdateTransactionDetailOption';
import {
  TransactionDetailOptionDrawerMode,
  TransactionDetailOptionFormType,
} from '@/features/transaction_items/Detail/type';
import { useCallback, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';

export type UseTransactionDetailOptionDrawerReturn = {
  // Drawer state
  isVisible: boolean;
  mode: TransactionDetailOptionDrawerMode;

  // Form methods
  methods: UseFormReturn<TransactionDetailOptionFormType>;

  // Drawer handlers
  openAddDrawer: () => void;
  openEditDrawer: (id: string, data: TransactionDetailOptionFormType) => void;
  closeDrawer: () => void;
  handleSave: (data: TransactionDetailOptionFormType) => void;
};

export const useTransactionDetailOptionDrawer =
  (): UseTransactionDetailOptionDrawerReturn => {
    // Drawer state
    const [isVisible, setIsVisible] = useState(false);
    const [mode, setMode] = useState<TransactionDetailOptionDrawerMode>(
      TransactionDetailOptionDrawerMode.ADD_MODE
    );
    const [editingId, setEditingId] = useState<string>('');

    const methods = useForm<TransactionDetailOptionFormType>({
      defaultValues: {
        code: '',
        label: '',
        priority: 100,
        is_visible: true,
      },
      mode: 'onSubmit',
    });

    const { onSubmitCreate } = useSubmitCreateTransactionDetailOption({
      setValidationError: methods.setError,
    });

    const { onSubmitUpdate } = useSubmitUpdateTransactionDetailOption({
      setValidationError: methods.setError,
    });

    // Open drawer for adding new option
    const openAddDrawer = useCallback(() => {
      setMode(TransactionDetailOptionDrawerMode.ADD_MODE);
      methods.reset({
        code: '',
        label: '',
        priority: 100,
        is_visible: true,
      });
      setIsVisible(true);
    }, [methods]);

    // Open drawer for editing existing option
    const openEditDrawer = useCallback(
      (id: string, data: TransactionDetailOptionFormType) => {
        setEditingId(id);
        setMode(TransactionDetailOptionDrawerMode.EDIT_MODE);
        methods.reset(data);
        setIsVisible(true);
      },
      [methods]
    );

    const closeDrawer = useCallback(() => {
      setIsVisible(false);
      setEditingId('');
      methods.reset();
    }, [methods]);

    const handleSave = useCallback(
      async (data: TransactionDetailOptionFormType) => {
        let isSuccess = false;

        if (mode === TransactionDetailOptionDrawerMode.ADD_MODE) {
          isSuccess = (await onSubmitCreate(data)) === true;
        } else if (mode === TransactionDetailOptionDrawerMode.EDIT_MODE) {
          isSuccess =
            (await onSubmitUpdate({ id: editingId, form: data })) === true;
        }

        if (isSuccess) {
          closeDrawer();
        }
      },
      [onSubmitCreate, onSubmitUpdate, closeDrawer, mode, editingId]
    );

    return {
      // State
      isVisible,
      mode,

      // Form methods
      methods,

      // Handlers
      openAddDrawer,
      openEditDrawer,
      closeDrawer,
      handleSave,
    };
  };
